import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Modal from '../../../../shared/modal';
import Input from '../../../../shared/input';
import Textarea from '../../../../shared/textarea';
import Button from '../../../../shared/button';
import Error from '../../../../shared/error';
import { IExpertPartner, IExpertPartnerFormValues } from '../../../../interfaces/expert-partners/IExpertPartner';
import { EXPERT_PARTNER_AGENTS } from '../constants';
import ColorPickerField from './ColorPickerField';

interface IManageExpertPartnerModal {
    openModal: boolean;
    item?: IExpertPartner;
    loading?: boolean;
    onSubmit: (payload: FormData) => Promise<void>;
    handleCloseModal: () => void;
}

const buildFormState = (item?: IExpertPartner): IExpertPartnerFormValues => ({
    _id: item?._id,
    id: item?.id,
    agent: item?.agent || '',
    companyName: item?.companyName || '',
    backgroundColor: item?.backgroundColor || '',
    borderColor: item?.borderColor || '',
    textColor: item?.textColor || '',
    contactUs: item?.contactUs || '',
    city: item?.city || '',
    country: item?.country || '',
    expertise: item?.expertise || '',
});

const isValidHexColor = (value: string) => /^#[0-9A-Fa-f]{6}$/.test(value);

const trimOptionalValue = (value?: string) => value?.trim() || '';

const getModalErrorMessage = (error: any) => {
    if (typeof error === 'string') return error;
    if (error?.data?.message) {
        return Array.isArray(error.data.message) ? error.data.message.join(', ') : error.data.message;
    }
    if (error?.message) return error.message;
    return 'Something went wrong while saving expert partner';
};

const ManageExpertPartnerModal = ({
    openModal,
    item,
    loading,
    onSubmit,
    handleCloseModal,
}: IManageExpertPartnerModal) => {
    const [form, setForm] = useState<IExpertPartnerFormValues>(buildFormState());
    const [submitError, setSubmitError] = useState<string>('');
    const [selectedFile, setSelectedFile] = useState<File | undefined>();
    const [filePreview, setFilePreview] = useState<string | undefined>();
    const [currentLogoUrl, setCurrentLogoUrl] = useState<string | undefined>();

    useEffect(() => {
        if (!openModal) return;
        setForm(buildFormState(item));
        setSelectedFile(undefined);
        setFilePreview(undefined);
        setCurrentLogoUrl(item?.logoUrl);
        setSubmitError('');
    }, [openModal, item]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (!acceptedFiles?.length) return;
        const file = acceptedFiles[0];
        const reader = new FileReader();

        reader.onload = (event: ProgressEvent<FileReader>) => {
            const result = event?.target?.result as string;
            setFilePreview(result);
            setSelectedFile(file);
            setSubmitError('');
        };
        reader.readAsDataURL(file);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        accept: {
            'image/*': [],
        },
    });

    const onChangeInput = (value: string | null | undefined, type: string) => {
        setForm((prev) => ({ ...prev, [type]: value || '' }));
    };

    const onChangeTextarea = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setForm((prev) => ({ ...prev, expertise: e.target.value }));
    };

    const onSave = async () => {
        if (loading) return;

        const companyName = trimOptionalValue(form.companyName);
        const agent = trimOptionalValue(form.agent);

        if (!companyName) {
            setSubmitError('Company name is mandatory');
            return;
        }

        if (!agent) {
            setSubmitError('Agent is mandatory');
            return;
        }

        if (!selectedFile && !currentLogoUrl) {
            setSubmitError('Logo is mandatory');
            return;
        }

        if (form.backgroundColor && !isValidHexColor(form.backgroundColor)) {
            setSubmitError('Background color must be a valid hex value');
            return;
        }

        if (form.borderColor && !isValidHexColor(form.borderColor)) {
            setSubmitError('Border color must be a valid hex value');
            return;
        }

        if (form.textColor && !isValidHexColor(form.textColor)) {
            setSubmitError('Text color must be a valid hex value');
            return;
        }

        const payload = new FormData();
        payload.append('agent', agent);
        payload.append('companyName', companyName);

        const backgroundColor = trimOptionalValue(form.backgroundColor);
        const borderColor = trimOptionalValue(form.borderColor);
        const textColor = trimOptionalValue(form.textColor);
        const contactUs = trimOptionalValue(form.contactUs);
        const city = trimOptionalValue(form.city);
        const country = trimOptionalValue(form.country);
        const expertise = trimOptionalValue(form.expertise);

        if (backgroundColor) payload.append('backgroundColor', backgroundColor);
        if (borderColor) payload.append('borderColor', borderColor);
        if (textColor) payload.append('textColor', textColor);
        if (contactUs) payload.append('contactUs', contactUs);
        if (city) payload.append('city', city);
        if (country) payload.append('country', country);
        if (expertise) payload.append('expertise', expertise);

        if (form.id) payload.append('id', form.id);
        if (form._id) {
            payload.append('_id', form._id);
        } else if (item?._id) {
            payload.append('_id', item._id);
        } else if (item?.id) {
            payload.append('_id', item.id);
        }

        if (selectedFile) {
            payload.append('file', selectedFile);
        }

        try {
            await onSubmit(payload);
            handleCloseModal();
        } catch (error) {
            setSubmitError(getModalErrorMessage(error));
        }
    };

    return (
        <Modal
            open={openModal}
            onClose={handleCloseModal}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[80%] lg:min-w-[900px]'
        >
            <div className='p-2 max-w-[98vw] min-w-[95%] md:min-w-[80%] lg:w-[860px]'>
                <p className='mb-4 font-semibold text-base dark:text-primary-dark'>
                    {item ? 'Edit Expert Partner' : 'Create Expert Partner'}
                </p>

                {submitError && <Error text={submitError} />}

                <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                    <div>
                        <div className='mb-3 w-full'>
                            <p className='mb-1 text-gray-600 font-medium text-sm dark:text-gray-300'>Agent</p>
                            <select
                                value={form.agent || ''}
                                onChange={(e) => onChangeInput(e.target.value, 'agent')}
                                className='select-primary dark:bg-black dark:text-white dark:border-gray-700'
                            >
                                <option value=''>Select Agent</option>
                                {EXPERT_PARTNER_AGENTS.map((agentItem) => (
                                    <option key={agentItem.value} value={agentItem.value} className='dark:bg-black dark:text-white'>
                                        {agentItem.label}
                                    </option>
                                ))}
                            </select>
                            <span className='block h-2 mt-2' />
                        </div>

                        <Input
                            label='Company Name'
                            placeholder=' '
                            inputValue={form.companyName}
                            showValue={true}
                            inputUniqueName='companyName'
                            onChangeInput={onChangeInput}
                        />

                        <Input
                            label='Contact Us'
                            placeholder=' '
                            inputValue={form.contactUs}
                            showValue={true}
                            inputUniqueName='contactUs'
                            onChangeInput={onChangeInput}
                        />

                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                            <Input
                                label='City'
                                placeholder=' '
                                inputValue={form.city}
                                showValue={true}
                                inputUniqueName='city'
                                onChangeInput={onChangeInput}
                            />
                            <Input
                                label='Country'
                                placeholder=' '
                                inputValue={form.country}
                                showValue={true}
                                inputUniqueName='country'
                                onChangeInput={onChangeInput}
                            />
                        </div>

                        <div className='mb-3'>
                            <p className='mb-1 text-primary-light font-normal text-base'>Expertise</p>
                            <Textarea
                                value={form.expertise}
                                rows={5}
                                hideMaxLength={true}
                                onChangeTextarea={onChangeTextarea}
                                className='textarea-primary !resize-y dark:bg-black dark:text-white dark:border-gray-700'
                            />
                        </div>
                    </div>

                    <div>
                        <ColorPickerField
                            label='Background Color'
                            value={form.backgroundColor}
                            onChange={(value) => onChangeInput(value, 'backgroundColor')}
                        />
                        <ColorPickerField
                            label='Border Color'
                            value={form.borderColor}
                            onChange={(value) => onChangeInput(value, 'borderColor')}
                        />
                        <ColorPickerField
                            label='Text Color'
                            value={form.textColor}
                            onChange={(value) => onChangeInput(value, 'textColor')}
                        />

                        <p className='mb-1 text-primary-light font-normal text-base'>Logo</p>
                        <div
                            {...getRootProps()}
                            className={`border border-dashed rounded-lg p-4 cursor-pointer transition-colors ${isDragActive ? 'border-[#6BAA75] bg-[#F3FDED] dark:bg-black' : 'border-gray-300 dark:border-gray-700'}`}
                        >
                            <input {...getInputProps()} />
                            <p className='text-sm text-gray-600 dark:text-gray-300'>
                                Drag and drop a logo here, or click to select a file
                            </p>
                        </div>

                        <div className='mt-3 flex flex-row items-center flex-wrap gap-3'>
                            {filePreview && (
                                <div className='relative flex flex-col items-center'>
                                    <div
                                        onClick={() => {
                                            setSelectedFile(undefined);
                                            setFilePreview(undefined);
                                        }}
                                        className='absolute top-[-8px] right-[-8px] cursor-pointer z-40'
                                    >
                                        <img src='/assets/shared/cancel.svg' className='max-w-[15px] object-contain' />
                                    </div>
                                    <div className='border border-gray-300 dark:border-gray-700 p-1 rounded'>
                                        <img src={filePreview} className='h-[80px] w-[80px] object-cover rounded' />
                                    </div>
                                    <p className='text-xs mt-1 text-gray-500 dark:text-gray-400'>New Logo</p>
                                </div>
                            )}

                            {!filePreview && currentLogoUrl && (
                                <div className='flex flex-col items-center'>
                                    <div className='border border-gray-300 dark:border-gray-700 p-1 rounded'>
                                        <img src={currentLogoUrl} className='h-[80px] w-[80px] object-cover rounded' />
                                    </div>
                                    <p className='text-xs mt-1 text-gray-500 dark:text-gray-400'>Current Logo</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className='flex flex-row justify-center mt-4'>
                    <Button
                        label={item ? 'Save Changes' : 'Create Expert Partner'}
                        className={`${loading ? 'btn-main-disable' : 'btn-main dark:!bg-black dark:!text-white dark:!border-black'} !shadow-none min-w-[200px]`}
                        disabled={loading}
                        onClickButton={onSave}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default ManageExpertPartnerModal;
